import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';
import { isProductConfigurable } from '@/util/isProductConfigurable';
import clsx from 'clsx';
import Link from 'next/link';
import React, { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import type { OptionsRoot, SwatchDataValue } from '../types/data';
import type { ConfigurableOption, Item, ProductRoot } from '../types/product';

type ColorOptionProps = {
  item: Item[] | ConfigurableOption[];
  hasOnlyOneColor: boolean;
  options: OptionsRoot | ConfigurableOption[];
  categoryUrlKey: string;
  outOfStockVariants: any;
  product: ProductRoot;
  showShopMoreInThisColor?: boolean;
  maybeTabs?: React.ReactNode;
  optionSpecs: any;
  clickIndex: number;
  handleClick: any;
  hasAvailableProduct: boolean;
  setSwatchAddToCartDialogVisible?: (nextVisible: boolean) => void;
  showSwatchAddToCartTrigger?: boolean;
  getIsOutOfStock: (formField: string, valueIndex: string) => boolean;
  onResetAvailableProductOtherAttr?: (nextColorValue: any) => void;
  setRelatedProductFilterColor?: Dispatch<SetStateAction<string>>;
};

export const ColorOptions = ({
  item,
  hasOnlyOneColor,
  options,
  categoryUrlKey,
  outOfStockVariants,
  product,
  showShopMoreInThisColor = true,
  maybeTabs,
  handleClick,
  clickIndex,
  optionSpecs,
  hasAvailableProduct,
  setSwatchAddToCartDialogVisible,
  showSwatchAddToCartTrigger = false,
  getIsOutOfStock,
  onResetAvailableProductOtherAttr,
  setRelatedProductFilterColor,
}: ColorOptionProps) => {
  const fabricName = item?.label.split('-')[1];
  const form = useFormContext();
  const { watch, setValue } = form;
  const selectedFabric = watch('Fabric');
  const allFormData = watch();

  const selectedColor = watch('Color');
  const oldValues = item?.values;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const variants = product?.variants || [];
  // 找到当前 color 的所有 variants
  const findVariants = useCallback(
    (item_value_index: number) => {
      const match =
        variants &&
        variants.filter(variant => {
          const { attributes } = variant || {};
          const { value_index: variant_value_index } = attributes[0] || {};

          return variant_value_index === item_value_index;
        });

      return match;
    },
    [variants],
  );

  // 将 outofstock 的放在后面
  const values = useMemo(() => {
    if (!isProductConfigurable(product)) {
      return oldValues;
    }

    const instockArray: any[] = [];
    const outofStockArray: any[] = [];

    oldValues?.forEach((item: any) => {
      const { value_index: item_value_index } = item || {};

      const match = findVariants(item_value_index);

      const allOutOfStock = match?.every((item: any) => item.product.stock_status === 'OUT_OF_STOCK');

      if (allOutOfStock) {
        outofStockArray.push(item);
      } else {
        instockArray.push(item);
      }
    });

    if (hasAvailableProduct) {
      return [...instockArray];
    }

    return [...instockArray, ...outofStockArray];
  }, [findVariants, hasAvailableProduct, oldValues, product]);

  if (
    !hasOnlyOneColor &&
    !(
      selectedFabric &&
      options
        .find(d => d.label === 'Fabric')
        ?.values.find(v => v.value_index.toString() === selectedFabric?.value_index)
        ?.label.includes(fabricName)
    )
  ) {
    return null;
  }

  const selectedColorItem = allFormData?.Color;

  return (
    <>
      <FormField
        key={item?.uid}
        control={form.control}
        name={'Color'}
        render={({ field }) => {
          return (
            <div>
              {maybeTabs}
              <FormItem className={clsx('bg-white  px-2.5 py-[15px] lg_p-0')}>
                <FormLabel
                  className='option_title__x4i8y mb-2.5 flex items-center justify-between '
                  id='Color'
                >
                  <div className='flex items-center gap-x-2.5'>
                    <span className='text-base uppercase leading-5'>Color: </span>
                    <span className=' text-[15px] leading-5 text-80583F'>{selectedColorItem?.label}</span>
                    {selectedColorItem?.price < 0 ? (
                      <div className='flex items-center'>
                        <div
                          className={clsx(
                            'h-0 w-0 ',
                            'border-b-[8px] border-t-[8px] border-b-transparent border-t-transparent',
                            'border-r-[6px] border-r-e73635',
                          )}
                        />
                        <div className='bg-e73635 py-0.5 pl-0.5 pr-[3px] text-xs font-medium leading-none text-white'>
                          {selectedColorItem?.price}USD
                        </div>
                      </div>
                    ) : null}
                  </div>

                  {showShopMoreInThisColor && selectedColorItem?.label && (
                    <Link
                      href={`/${categoryUrlKey}/color/${selectedColorItem?.label.toLowerCase().replace(/\s+/g, '-')}`}
                      className='text-[15px] leading-5 underline lg_hover_text-80583F lg_hover_decoration-80583F'
                    >
                      Shop More In This Color
                    </Link>
                  )}
                </FormLabel>
                <FormMessage />
                <FormControl>
                  <RadioGroup
                    suppressHydrationWarning
                    onValueChange={value => {
                      const selectedOption = values.find(v => v.value_index.toString() === value);

                      onResetAvailableProductOtherAttr?.({
                        value_index: value,
                        label: selectedOption?.label,
                        price: selectedOption?.price,
                        option_id: selectedOption?.option_id?.toString() || item?.attribute_id,
                      });
                      setRelatedProductFilterColor?.(selectedOption?.label || '');

                      if (selectedOption) {
                        field.onChange({
                          value_index: value,
                          label: selectedOption?.label,
                          price: selectedOption?.price,
                          option_id: selectedOption?.option_id?.toString() || item?.attribute_id,
                        });
                      }

                      const shouldAddToSpec = optionSpecs.includes(selectedOption?.value_index.toString());

                      if (shouldAddToSpec) {
                        handleClick({
                          bool: true,
                          text: selectedOption?.value_index?.toString(),
                          index: clickIndex,
                        });
                      } else {
                        handleClick({
                          bool: true,
                          text: '',
                          index: clickIndex,
                        });
                      }
                    }}
                    value={field.value?.value_index}
                    className='flex flex-wrap gap-x-0.5 gap-y-[5px]'
                  >
                    {values.map(option => (
                      <ColorRadioItem
                        key={option.uid}
                        item={option}
                        outOfStockVariants={outOfStockVariants}
                        optionSpecs={optionSpecs}
                        field={field}
                        hasAvailableProduct={hasAvailableProduct}
                        isOutOfStock={getIsOutOfStock('Color', option.value_index)}
                      />
                    ))}
                    {showSwatchAddToCartTrigger ? (
                      <button
                        type='button'
                        className='ml-3 text-[15px] leading-5 underline lg_hover_text-80583F lg_hover_decoration-80583F'
                        onClick={() => {
                          setSwatchAddToCartDialogVisible?.(true);
                        }}
                      >
                        Order Free Swatch
                      </button>
                    ) : null}
                  </RadioGroup>
                </FormControl>
              </FormItem>
            </div>
          );
        }}
      />
    </>
  );
};

const ColorRadioItem = ({
  item,
  outOfStockVariants,
  optionSpecs,
  field,
  hasAvailableProduct,
  isOutOfStock,
}: {
  item: SwatchDataValue;
  outOfStockVariants: any;
  optionSpecs: any;
  field: any;
  hasAvailableProduct: boolean;
  isOutOfStock: boolean;
}) => {
  // const inStock = optionSpecs.includes(item.value_index.toString());

  // const isOutOfStock = outOfStockVariants.flat().includes(item.value_index);

  const { thumbnail, value } = item?.swatch_data || {};

  let swatchValue = '';

  // 在thumbnail存在的情况下如果thumbnail值包含#则是色值否则是图片
  if (thumbnail && thumbnail.indexOf('#') < 0) {
    if (thumbnail.indexOf('https://magento') === 0) {
      swatchValue = `url(${thumbnail.replace(process.env.NEXT_PUBLIC_MAGENTO_BACKEND_URL, '/')})`;
    } else {
      swatchValue = `url("${process.env.NEXT_PUBLIC_BASE_PATH}/media/attribute/swatch/swatch_thumb/110x90${thumbnail}")`;
    }
  } else {
    swatchValue = value as string;
  }

  const finalStyle = Object.assign(
    {},
    {
      '--venia-swatch-bg': swatchValue,
      background: swatchValue,
      backgroundSize: 'contain !important',
    },
  );

  const colorPrice = item?.price;

  const shouldShowPriceTag = typeof colorPrice === 'number' && colorPrice < 0;

  return (
    <FormItem className={cn('flex items-center space-y-0', isOutOfStock && hasAvailableProduct && 'hidden')}>
      <FormControl>
        <RadioGroupItem
          value={item.value_index.toString()}
          className='peer sr-only'
          suppressHydrationWarning
        />
      </FormControl>
      <FormLabel
        className={cn(
          'flex cursor-pointer items-center justify-between rounded-full border border-solid border-white p-[4px] hover_border-primary',
          field.value?.value_index === item.value_index.toString() ? 'border-primary' : '',
        )}
      >
        <div
          className={cn(
            'relative h-8 w-8 rounded-full !bg-contain',
            item?.swatch_data?.value === '#ffffff' || item?.swatch_data?.value === '#FFFFFF'
              ? 'border-1 border-E7DED5'
              : '',
            isOutOfStock && 'opacity-50',
            isOutOfStock && hasAvailableProduct && 'hidden opacity-50',
          )}
          style={finalStyle}
          data-cy='Swatch-root'
        >
          {isOutOfStock && (
            <div className='absolute  left-1/2 top-1/2 z-[999] h-px w-full -translate-x-1/2 -translate-y-1/2 rotate-45 bg-white' />
          )}
          {shouldShowPriceTag ? (
            <div className={clsx(' text-sm text-white', 'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2')}>
              Sale
            </div>
          ) : null}
        </div>
      </FormLabel>
    </FormItem>
  );
};
